import {
  type ApplicationValidationError,
  type DependencyValidationError,
  isLargeOperation,
  type ParameterDefinitionCreateOperation,
} from '@festina/configuration-general-types/configuration'
import { dateUtils, type Optional } from '@festina-life/core'

import type { ParameterConfigurationItem } from '../../types/common'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { useConfigurationOperationErrorsMap } from '../../uiModels/errors/useConfigurationOperationErrorsMap'
import type { ParameterValueOperation } from '../../uiModels/parameterOperations/types'
import type { OperationWithSourceInfo } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { LocationAwareOperationLink } from '../configurationOperationLink/LocationAwareOperationLink'
import {
  listIdsWithOperationsOrErrors,
  resolveOperationLocation,
} from './helpers'
import type { OperationsListVariant } from './types'

type Props<
  T extends ParameterDefinitionCreateOperation | ParameterValueOperation,
> = {
  operations: OperationWithSourceInfo<T>[]
  errors: ValidationErrorWithLocation<
    DependencyValidationError | ApplicationValidationError
  >[]
  parameterName: (operation: T) => string
  fromDate?: (operation: T) => Optional<Date>
  toDate?: (operation: T) => Optional<Date>
  operationType: ParameterConfigurationItem
  variant: OperationsListVariant
}

export const ParameterOperationsWithErrors = <
  T extends ParameterDefinitionCreateOperation | ParameterValueOperation,
>({
  operations,
  errors,
  parameterName,
  operationType,
  fromDate,
  toDate,
  variant,
}: Props<T>) => {
  const { workspaceKey, branchKey } = useOperationsContext()

  const parameterErrors = useConfigurationOperationErrorsMap({
    errors,
    operations,
    idFromOperation: (operation) => parameterName(operation.internalOperation),
    idFromError: (error) => error.operationName ?? '',
  })

  const allParameterNames = listIdsWithOperationsOrErrors(
    operations.map((operation) => parameterName(operation.internalOperation)),
    parameterErrors,
  )

  return allParameterNames.map((parameterNameValue) => {
    const parameterOperations = operations.filter(
      (operation) =>
        parameterName(operation.internalOperation) === parameterNameValue,
    )
    const operationErrors = parameterErrors?.get(parameterNameValue)

    const operationKeySet = listIdsWithOperationsOrErrors(
      parameterOperations.map((operation) =>
        isLargeOperation(operation.internalOperation)
          ? operation.internalOperation.innerOperationKey
          : operation.internalOperation.operationKey,
      ),
      operationErrors,
    )

    return operationKeySet.map((operationKey) => {
      const parameterOperation = parameterOperations?.find(
        ({ internalOperation }) =>
          internalOperation.operationKey === operationKey,
      )
      const parameterErrors = operationErrors?.get(operationKey)
      const errorCount = parameterErrors?.length ?? 0

      const itemName = parameterOperation
        ? parameterName(parameterOperation.internalOperation)
        : (parameterErrors?.[0]?.error.operationName ?? '')
      const fromDateString = parameterOperation
        ? fromDate?.(parameterOperation.internalOperation)
        : parameterErrors?.[0]?.error.operationFromDate
      const fromDateValue = fromDateString
        ? dateUtils.dateUtcAsLocal(fromDateString).toDate()
        : undefined

      const toDateString = parameterOperation
        ? toDate?.(parameterOperation.internalOperation)
        : parameterErrors?.[0]?.error.operationToDate
      const toDateValue = toDateString
        ? dateUtils.dateUtcAsLocal(toDateString).toDate()
        : undefined

      const shouldHideOperationLink =
        variant === 'errors' ? !errorCount : !parameterOperation

      // If there is no parameterOperation the only way the operationKey can be processed is if there's at least one error with that key.
      const { layer, block } = resolveOperationLocation(
        parameterOperation,
        parameterErrors,
      )

      return shouldHideOperationLink ? null : (
        <LocationAwareOperationLink
          // oxlint-disable-next-line react/no-array-index-key
          key={operationKey}
          layer={layer}
          block={block}
          itemId={itemName}
          type={operationType}
          workspaceKey={parameterOperation ? workspaceKey : undefined} // If there's no operation then the operation belongs to the branch and not the workspace
          branchKey={workspaceKey?.branchKey ?? branchKey}
          isLoading={false}
          errorCount={errorCount}
          operation={parameterOperation?.internalOperation}
          fromDate={fromDateValue}
          toDate={toDateValue}
          isDeleteEnabled={variant === 'operations'}
          operationKey={operationKey}
          isDraft={!!parameterOperation?.isDraft}
        />
      )
    })
  })
}
